import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown } from 'react-icons/fi';

import { ISelect } from '@pra-vendas-themes/interfaces/ISelect';

import { useEventListener } from 'hooks/useEventListener';

import { withIsMounted } from 'utils/withIsMounted';

import {
  StyledInput,
  WrapperInput,
  WrapperIcon,
  Dropdown,
  Option,
} from './styles';

const Select: FC<ISelect> = withIsMounted(
  ({
    placeholder,
    disabled = false,
    color = 'primary',
    name,
    autoFocus = false,
    onChange,
    onBlur,
    value,
    type,
    as,
    maxLength,
    onKeyPress,
    options,
    getOptionLabel,
    getValueLabel,
  }) => {
    const selectRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [clientRect, setClientRect] = useState<DOMRect>();

    const hasIcon = useMemo(() => {
      return type === 'password';
    }, [type]);

    const handleClickSelect = (event: any): void => {
      if (event.target.id.includes('option')) return;
      setIsOpen((prev) => !prev);
    };

    const handleClickOption = (option: any): void => {
      onChange?.({ target: { name, value: option } });
      setIsOpen(false);
    };

    const handleSetClientRect = (): void => {
      setClientRect(selectRef.current?.getBoundingClientRect());
    };

    useEffect(() => {
      const closeDropdown = (event: any): void => {
        if (!selectRef.current?.contains(event.target)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        handleSetClientRect();

        document.addEventListener('mousedown', closeDropdown);
      } else {
        document.removeEventListener('mousedown', closeDropdown);
      }

      return () => {
        document.removeEventListener('mousedown', closeDropdown);
      };
    }, [isOpen]);

    useEventListener('resize', handleSetClientRect);
    useEventListener(
      'scroll',
      () => setIsOpen(false),
      document.getElementById('main-element')
    );

    return (
      <WrapperInput ref={selectRef} onClick={handleClickSelect}>
        <StyledInput
          as={as}
          hasIcon={hasIcon}
          autoFocus={autoFocus}
          id={name}
          name={name}
          color={color}
          placeholder={placeholder}
          disabled={disabled}
          // onChange={onChange}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
          value={getValueLabel(value)}
          maxLength={maxLength}
          readOnly
        />
        <WrapperIcon onClick={handleClickSelect}>
          <FiChevronDown />
        </WrapperIcon>

        {createPortal(
          <Dropdown
            className={isOpen ? 'isOpen' : ''}
            selectClientRect={clientRect}
          >
            {options?.map((option) => (
              <Option
                id={`option-${getValueLabel(option)}`}
                key={getValueLabel(option)}
                onClick={() => handleClickOption(option)}
                className={value === option ? 'isActive' : ''}
              >
                {getOptionLabel(option)}
              </Option>
            ))}
          </Dropdown>,
          document.getElementById('portal-root') as Element
        )}
      </WrapperInput>
    );
  }
);

export default Select;
